import { cn } from "cn";
import { SquarePen } from "lucide-react";
import type * as React from "react";
import { useEffect, useId, useLayoutEffect, useRef } from "react";
import { REVIEW_PRIMARY, REVIEW_QUIET } from "./review-card";

// Edit one sentence in place (apps/web pages/Visit/workspace/SentenceEditor):
// the review card's frame with a text box instead of the flagged value.
// Enter saves, Shift+Enter adds a line, Escape cancels. Saving it empty
// removes the sentence, so the main button then says so. As a card next to
// the sentence from 1024px, a bottom sheet on phones. Positioning and
// saving stay with the caller.

interface InlineEditCardProps {
  title: React.ReactNode;
  /** The text box's accessible name. */
  label: string;
  value: string;
  onValueChange: (value: string) => void;
  onSave: () => void;
  onCancel: () => void;
  saveLabel: string;
  /** The main button's label while the text is empty (saving removes). */
  removeLabel: string;
  cancelLabel: string;
  /** Keyboard hint under the text box (desktop). */
  hint?: React.ReactNode;
  /** A quiet link on the left of the actions ("Edit whole note"). */
  secondary?: React.ReactNode;
  busy?: boolean;
  /** "card": floating card (position it with style). "sheet": bottom sheet. */
  layout?: "card" | "sheet";
  style?: React.CSSProperties;
  className?: string;
  ref?: React.Ref<HTMLDivElement>;
}

// A quiet text link for the card's secondary action.
export const INLINE_EDIT_LINK =
  "inline-flex h-11 items-center rounded-full px-1 text-[13.5px] font-medium text-v3-text-2 underline-offset-4 transition-colors hover:text-v3-text hover:underline focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-v3-brand-ring disabled:pointer-events-none disabled:opacity-50 lg:h-10";

export function InlineEditCard({
  title,
  label,
  value,
  onValueChange,
  onSave,
  onCancel,
  saveLabel,
  removeLabel,
  cancelLabel,
  hint,
  secondary,
  busy = false,
  layout = "card",
  style,
  className,
  ref,
}: InlineEditCardProps): React.JSX.Element {
  const titleId = useId();
  const inputId = useId();
  const input = useRef<HTMLTextAreaElement>(null);
  const empty = !value.trim();

  // Opens with the whole sentence selected, ready to type over or fix.
  useEffect(() => {
    const box = input.current;
    if (!box) return;
    box.focus({ preventScroll: true });
    box.setSelectionRange(box.value.length, box.value.length);
  }, []);

  // Grows with the text (up to a few lines, then scrolls).
  // biome-ignore lint/correctness/useExhaustiveDependencies: re-measured per value.
  useLayoutEffect(() => {
    const box = input.current;
    if (!box) return;
    box.style.height = "auto";
    box.style.height = `${Math.min(box.scrollHeight, 220)}px`;
  }, [value]);

  return (
    <div
      ref={ref}
      aria-labelledby={titleId}
      className={cn(
        "z-50 bg-v3-panel font-v3 text-v3-text",
        layout === "sheet"
          ? "fixed inset-x-0 bottom-0 max-h-[75dvh] overflow-y-auto rounded-t-[22px] px-4 pt-2 pb-[max(1rem,env(safe-area-inset-bottom))] shadow-v3-float"
          : "rounded-[18px] p-4 shadow-v3-pop",
        className,
      )}
      data-testid="sentence-editor"
      role="dialog"
      style={style}
    >
      {layout === "sheet" && (
        <div
          aria-hidden
          className="mx-auto mb-2 h-1 w-10 rounded-full bg-v3-surface-3"
        />
      )}
      <h2
        className="flex items-center gap-2 text-[15px] font-semibold text-v3-text"
        id={titleId}
      >
        <SquarePen aria-hidden className="size-4 text-v3-brand" />
        {title}
      </h2>
      <form
        className="mt-3"
        onSubmit={(event) => {
          event.preventDefault();
          if (!busy) onSave();
        }}
      >
        <label className="sr-only" htmlFor={inputId}>
          {label}
        </label>
        <textarea
          ref={input}
          className="block max-h-[220px] min-h-11 w-full resize-none rounded-xl border-0 bg-v3-surface-2 px-3.5 py-2.5 text-[16px] leading-relaxed text-v3-text shadow-v3-hairline outline-hidden focus:ring-2 focus:ring-v3-brand-ring disabled:opacity-60 lg:text-[15px]"
          data-testid="sentence-editor-input"
          disabled={busy}
          id={inputId}
          rows={1}
          value={value}
          onChange={(event) => onValueChange(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Escape") {
              event.preventDefault();
              event.stopPropagation();
              if (!busy) onCancel();
              return;
            }
            // Enter saves; Shift+Enter is a new line; Enter that confirms an
            // IME composition (Japanese, Chinese) does neither.
            if (
              event.key === "Enter" &&
              !event.shiftKey &&
              !event.nativeEvent.isComposing &&
              event.keyCode !== 229
            ) {
              event.preventDefault();
              if (!busy) onSave();
            }
          }}
        />
        {hint && (
          <p className="mt-2 text-[12.5px] leading-snug text-v3-text-3 max-lg:hidden">
            {hint}
          </p>
        )}
        <div className="mt-3 flex flex-wrap items-center gap-2">
          {secondary && <div className="mr-auto">{secondary}</div>}
          <button
            className={cn(REVIEW_QUIET, !secondary && "ml-auto")}
            data-testid="sentence-editor-cancel"
            disabled={busy}
            type="button"
            onClick={onCancel}
          >
            {cancelLabel}
          </button>
          <button
            className={REVIEW_PRIMARY}
            data-empty={empty ? "true" : undefined}
            data-testid="sentence-editor-save"
            disabled={busy}
            type="submit"
          >
            {empty ? removeLabel : saveLabel}
          </button>
        </div>
      </form>
    </div>
  );
}
